import React, { forwardRef, ButtonHTMLAttributes, ReactNode } from 'react';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Button variant style */
  variant?: 'primary' | 'secondary' | 'outline' | 'outline-secondary' | 'ghost' | 'danger' | 'success';
  /** Button size */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Full width button */
  fullWidth?: boolean;
  /** Show loading spinner */
  loading?: boolean;
  /** Icon to display before text */
  leftIcon?: ReactNode;
  /** Icon to display after text */
  rightIcon?: ReactNode;
  /** Additional CSS classes */
  className?: string;
  /** Button content */
  children: ReactNode;
}

const variantStyles: Record<string, string> = {
  primary: `
    bg-primary-600 bg-gradient-to-r from-primary-500 to-primary-600 text-white
    shadow-sm shadow-primary
    hover:from-primary-400 hover:to-primary-500 hover:shadow-md hover:-translate-y-0.5
    active:translate-y-0 active:shadow-sm
    focus-visible:ring-primary-500
    disabled:from-primary-300 disabled:to-primary-400
  `,
  secondary: `
    bg-secondary-600 bg-gradient-to-r from-secondary-500 to-secondary-600 text-white
    shadow-sm shadow-secondary
    hover:from-secondary-400 hover:to-secondary-500 hover:shadow-md hover:-translate-y-0.5
    active:translate-y-0 active:shadow-sm
    focus-visible:ring-secondary-500
    disabled:from-secondary-300 disabled:to-secondary-400
  `,
  outline: `
    bg-transparent border-2 border-primary-500 text-primary-600
    hover:bg-primary-500 hover:text-white hover:shadow-primary
    active:bg-primary-600
    focus-visible:ring-primary-500
    disabled:border-primary-300 disabled:text-primary-300
  `,
  'outline-secondary': `
    bg-transparent border-2 border-secondary-500 text-secondary-600
    hover:bg-secondary-500 hover:text-white hover:shadow-secondary
    active:bg-secondary-600
    focus-visible:ring-secondary-500
    disabled:border-secondary-300 disabled:text-secondary-300
  `,
  ghost: `
    bg-transparent text-gray-600
    hover:bg-gray-100 hover:text-gray-900
    active:bg-gray-200
    dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100
    focus-visible:ring-gray-400
  `,
  danger: `
    bg-gradient-to-r from-red-500 to-red-600 text-white
    shadow-sm shadow-red-500/25
    hover:from-red-400 hover:to-red-500 hover:shadow-md hover:-translate-y-0.5
    active:translate-y-0 active:shadow-sm
    focus-visible:ring-red-500
    disabled:from-red-300 disabled:to-red-400
  `,
  success: `
    bg-gradient-to-r from-emerald-500 to-emerald-600 text-white
    shadow-sm shadow-emerald-500/25
    hover:from-emerald-400 hover:to-emerald-500 hover:shadow-md hover:-translate-y-0.5
    active:translate-y-0 active:shadow-sm
    focus-visible:ring-emerald-500
    disabled:from-emerald-300 disabled:to-emerald-400
  `,
};

const sizeStyles: Record<string, string> = {
  xs: 'h-7 px-3 text-xs rounded-lg gap-1',
  sm: 'h-9 px-4 text-sm rounded-xl gap-1.5',
  md: 'h-11 px-6 text-sm rounded-xl gap-2',
  lg: 'h-13 px-8 text-base rounded-xl gap-2',
  xl: 'h-14 px-10 text-lg rounded-2xl gap-3',
};

/**
 * Modern Button component with multiple variants, sizes, and states
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      loading = false,
      leftIcon,
      rightIcon,
      className = '',
      children,
      disabled,
      type = 'button',
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    const baseStyles = `
      inline-flex items-center justify-center
      font-semibold
      transition-all duration-150 ease-out
      focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2
      disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none
    `;

    const classes = [
      baseStyles,
      variantStyles[variant],
      sizeStyles[size],
      fullWidth ? 'w-full' : '',
      loading ? 'relative text-transparent' : '',
      className,
    ]
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

    return (
      <button
        ref={ref}
        type={type === 'submit' ? 'submit' : 'button'}
        disabled={isDisabled}
        className={classes}
        {...props}
      >
        {/* Loading spinner */}
        {loading && (
          <span className='absolute inset-0 flex items-center justify-center'>
            <svg
              className='animate-spin h-5 w-5 text-current'
              xmlns='http://www.w3.org/2000/svg'
              fill='none'
              viewBox='0 0 24 24'
            >
              <circle
                className='opacity-25'
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                strokeWidth='4'
              />
              <path
                className='opacity-75'
                fill='currentColor'
                d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
              />
            </svg>
          </span>
        )}

        {/* Left icon */}
        {leftIcon && !loading && (
          <span className='flex-shrink-0'>{leftIcon}</span>
        )}

        {/* Button text */}
        <span>{children}</span>

        {/* Right icon */}
        {rightIcon && !loading && (
          <span className='flex-shrink-0'>{rightIcon}</span>
        )}
      </button>
    );
  }
);

Button.displayName = 'Button';

export default Button;
